import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import { usePrevious } from '../../hooks';
import 'aframe';
import HotspotWrapperContainer from '../Hotspot/HotspotWrapperContainer';

import { FADE_OUT_TIME, FADE_IN_TIME } from './components/AFrameShared';
import { useSelector } from 'react-redux';

export const longestSide = 16;
const imageDistance = -8; // 8 back just like hotspots

export const getCameraDistance = (width, height, innerWidthOffset = 0) => {
  const deviceRatio =
    (window.innerWidth - innerWidthOffset) / window.innerHeight;
  const imageRatio = width / height;
  const fov = 80; // aframe default fov
  const useSide = width > height ? width : height;
  const distance = Math.abs(useSide / 2 / Math.tan(fov / 2));
  return (
    (deviceRatio > imageRatio
      ? distance / imageRatio // stop adjusting distance once this is hit
      : distance / deviceRatio) +
    imageDistance +
    1 // slight offset
  );
};

// one side will always be 16 depending on image orientation
// distance from the camera
export const getImageWidthHeightInMeters = (pixelWidth, pixelHeight) => {
  const ratio = pixelWidth / pixelHeight;
  let width = longestSide;
  let height = longestSide;
  if (ratio !== 1) {
    width = ratio > 1 ? longestSide : longestSide * ratio;
    height = ratio > 1 ? longestSide / ratio : longestSide;
  }
  return { width, height };
};

const AframeStatic = props => {
  const {
    changeStop,
    children,
    currentStop,
    currentTour,
    hotspotId,
    hotspots,
    isAddHotspot,
    panoId,
    style,
    previewTour,
    accountPermissions = {},
    alt,
  } = props;

  const { stops } = useSelector(state => state.stops);
  const publicTour = useSelector(state => state.publicTour);
  const location = useLocation();
  const [showHotspots, setShowHotspots] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});
  const [screenSize, setScreenSize] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);
  const imageId = `static-image-${panoId}`;

  const prevPanoId = usePrevious(panoId);
  const prevImageLoaded = usePrevious(imageLoaded);
  const prevScreenSize = usePrevious(screenSize);

  const aframeImageRef = useRef();

  const { pathname } = useLocation();

  useEffect(() => {
    // keep track of screen size for hotspot positioning

    const handleScreenSize = () => {
      setScreenSize(window.innerWidth);
    };

    const aImage = aframeImageRef.current;

    aImage &&
    aImage.addEventListener('animationcomplete__fade-in', () =>
      setShowHotspots(true)
    );

    window && window.addEventListener('resize', handleScreenSize);

    return () => {
      aImage &&
      aImage.removeEventListener('animationcomplete__fade-in', () =>
        setShowHotspots(false)
      );
      window && window.removeEventListener('resize', handleScreenSize);
    };
  }, [])

  useEffect(() => {
    setScreenSize(window.innerWidth);
    const initializeImageDimensions = () => {
      if (imageLoaded[panoId]) {
        const image = document.getElementById(imageId);
        if (image?.naturalWidth && image?.naturalHeight) {
          setImageHeight(image.naturalHeight);
          setImageWidth(image.naturalWidth);
        }

        aframeImageRef.current && aframeImageRef.current.setAttribute('src', `#${imageId}`);
      }
    };

    initializeImageDimensions();
  }, [
    hotspots,
    prevImageLoaded,
    imageLoaded,
    prevPanoId,
    panoId,
    imageId,
    showHotspots,
    setShowHotspots,
    aframeImageRef,
    screenSize,
    prevScreenSize,
  ]);

  const getTourStops = () => {
    let tourStops = [];

    if (currentTour) {
      if (publicTour?.stops) {
        tourStops = Object.values(publicTour.stops);
      }
      else if (previewTour || location.pathname.includes('hotspots-manager')) {
        tourStops = currentTour.stopIds
          .filter(id => stops[id])
          .map(id => stops[id]);
      } else {
        tourStops = currentTour.tourStops;
      }
    }

    return tourStops;
  };

  const { width, height } = getImageWidthHeightInMeters(
    imageWidth,
    imageHeight
  );

  const currentHotspots = hotspots || (currentStop && currentStop.hotspots);
  const hasHotspots =
    currentHotspots && Object.keys(currentHotspots).length > 0;
  const sideBarOffset =
    location.pathname.includes('hotspots-manager') &&
    document.getElementById('sidebar-wrapper-hotspots-manager').offsetWidth;
  const cameraDistance = getCameraDistance(width, height, sideBarOffset);

  const renderAssets = () => {
    const tourStops = getTourStops() || [];

    return tourStops
      .filter(stop => stop.mainMediaType === 'images')
      .map(stop => {
        const imageSrc = `https://res.cloudinary.com/concept3d/image/upload/q_auto,f_auto,b_black,c_lfill,dpr_1.0,g_center/v1/images/${stop.panoId}`;
        return (
          <img
            key={stop.panoId}
            id={`static-image-${stop.panoId}`}
            src={imageSrc}
            alt=""
            onLoad={() =>
              setImageLoaded({ ...imageLoaded, [stop.panoId]: true })
            }
            crossOrigin='anonymous'
          />
        );
      });
  };

  const hotspotManagerPreviewClass = cx({
    'hotspots-manager-preview': pathname.includes('hotspots-manager'),
  });

  const shouldShowHotspots =
    showHotspots && hasHotspots && accountPermissions.hotspots;
  let iosStyle = {};

  if (AFRAME.utils.device.isIOS()) {
    iosStyle = {
      ...style,
      position: 'fixed',
    };
  }

  return (
    <a-scene
      style={{ ...style, iosStyle }}
      id="scene"
      cursor="rayOrigin: mouse; fuseTimeout: 0;"
      class={hotspotManagerPreviewClass}
      raycaster="objects: .clickable;"
      vr-mode-ui="enabled: false"
      device-orientation-permission-ui="enabled: false"
      loading-screen="enabled: false"
      background="color: black"
      role="region"
      aria-label='Interactive image viewer'
      embedded
    >
      {children}
      <a-assets>{currentTour && renderAssets()}</a-assets>
      <a-image
        key={imageId}
        id="sky"
        ref={aframeImageRef}
        width={width}
        height={height}
        position={`0 0 ${imageDistance}`}
        material="transparent: true; depthWrite: false;"
        opacity="0"
        animation__fade-in={`property: opacity; to: 1; dur: ${FADE_IN_TIME}; startEvents: loaded;`}
        animation__fade-out={`property: opacity; to: 0; dur: ${FADE_OUT_TIME}; startEvents: fadeout;`}
        aria-label={alt}
        role="img"
      />
      {shouldShowHotspots && (
        <HotspotWrapperContainer
          changeStop={changeStop}
          hotspotId={hotspotId}
          hotspots={currentHotspots}
          isAddHotspot={isAddHotspot}
          tourId={currentTour?.id}
          tourName={currentTour?.name}
          showHotspotModal={hasHotspots}
          hotspotIcons={currentTour?.styles?.hotspotIcons}
          isStatic
        />
      )}
      <a-camera
        look-controls-enabled="false"
        wasd-controls-enabled="false"
        id="camera"
        position={`0 0 ${cameraDistance}`}
      />
    </a-scene>
  );
};

export default AframeStatic;
